import { useGate, useUnit } from 'effector-react';
import { Autocomplete, type AutocompleteProps, TextField } from '@mui/material';
import {
  ControlWrapper,
  getDefaultInputPropsAutocomplete,
  getAutocompleteOnChange,
  getAutocompleteValue,
  withControl,
} from '@unicorn/control';
import { SourceTypesControlGate, sourceTypesQuery } from './model';
import type { SourceType } from './types';

export const SourceTypesControl = withControl<
  Partial<AutocompleteProps<SourceType, boolean, boolean, boolean>>
>(
  ({
    label,
    value,
    setValue,
    error,
    isError,
    required,
    readOnly,
    disabled,
    helperText,
    multiple,
    clearable,
    placeholder,
    ...props
  }) => {
    useGate(SourceTypesControlGate);
    const { data, pending } = useUnit(sourceTypesQuery);

    const options = Array.isArray(data) ? data : [];

    const handleChange = (
      _: unknown,
      newValue: string | SourceType | (string | SourceType)[] | null
    ) => getAutocompleteOnChange(newValue, setValue, multiple);

    return (
      <ControlWrapper
        label={label}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        isError={isError}
        error={error}
        helperText={helperText}
      >
        <Autocomplete
          value={getAutocompleteValue(options, value, multiple)}
          onChange={handleChange}
          options={options}
          multiple={multiple}
          loading={pending}
          disableClearable={!clearable}
          renderInput={(params) => {
            const slotProps = {
              input: getDefaultInputPropsAutocomplete({
                params,
                readOnly,
                disabled,
                pending,
              }),
            };

            return (
              <TextField
                {...params}
                error={isError}
                placeholder={
                  multiple && Array.isArray(value) && value.length > 0 ? '' : placeholder
                }
                slotProps={slotProps}
              />
            );
          }}
          readOnly={readOnly}
          disabled={disabled}
          {...props}
        />
      </ControlWrapper>
    );
  }
);
